/**
 * Welcher Bereich der Landingpage gesperrt ist, während jemand daran arbeitet.
 *
 * **Warum das nötig ist.** Der Merge auf dem Server behandelt Listen als
 * Block: Ändert sich irgendetwas in `textSection`, gilt die ganze Liste als
 * eine Änderung. Zwei Leute an *verschiedenen* Textabschnitten derselben
 * Seite bekommen deshalb beim Speichern einen harten Konflikt, und der
 * Zweite verliert seine Arbeit.
 *
 * Solange sich nicht gezielt nur der geänderte Eintrag übertragen lässt,
 * muss die Sperre denselben Zuschnitt haben wie der Konflikt: die ganze
 * Liste. Dann erfährt der Zweite es vorher statt hinterher.
 *
 * Bewusst Vue-frei, damit es im Läufer unter resources/js/tests geprüft
 * werden kann.
 *
 * @see app/Services/CampaignJsonMergeService.php -- die Gegenstelle
 */

/**
 * Der gesperrte Bereich eines Pfades.
 *
 * Abgeschnitten wird an der ersten Klammer, denn ab dort geht es um einen
 * einzelnen Eintrag einer Liste -- und den kann der Server nicht getrennt
 * verrechnen. Beide Schreibweisen kommen vor:
 *
 *   $.campaign.components.callToAction[0]                    -> ...callToAction
 *   $.campaign.components.textSection[?(@.id=='greeting')]   -> ...textSection
 *   $.campaign.components.imageSlider.images[2].img          -> ...imageSlider.images
 *   $.campaign.components.globalHeader                       -> unverändert
 *
 * @param {string} path
 * @returns {string}
 */
export function lockScope(path) {
    if (typeof path !== 'string' || path === '') {
        return '';
    }

    const klammer = path.indexOf('[');

    return klammer === -1 ? path : path.slice(0, klammer);
}

/**
 * Überschneiden sich zwei Bearbeitungen?
 *
 * Gleicher Bereich oder Eltern-/Kind-Verhältnis. Letzteres, weil auch der
 * Merge einen Elternpfad als Überschneidung wertet: Wer `imageSlider`
 * bearbeitet, fasst auch `imageSlider.images` an.
 *
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function scopesCollide(a, b) {
    const sa = lockScope(a);
    const sb = lockScope(b);

    if (sa === '' || sb === '') {
        return false;
    }

    return sa === sb
        || sa.startsWith(sb + '.')
        || sb.startsWith(sa + '.');
}

/**
 * Alle Markierungen, die diesem Benutzer gehören.
 *
 * Gebraucht, wenn jemand den Kanal verlässt: Seine Markierungen müssen weg,
 * sonst gilt der Bereich weiter als belegt und niemand kommt mehr hinein.
 *
 * **Alle, nicht nur die erste.** Ein Benutzer kann mehrere halten -- der
 * Komponenten-Editor markiert den Pfad der Komponente, und jedes Textfeld
 * darin markiert zusätzlich seinen eigenen. Wer nach der ersten abbrach,
 * liess die übrigen stehen.
 *
 * @param {Object} fieldsFocusedBy Pfad => Benutzerkennung
 * @param {*}      userId
 * @returns {string[]}
 */
export function focusPathsOf(fieldsFocusedBy, userId) {
    if (! fieldsFocusedBy || typeof fieldsFocusedBy !== 'object') {
        return [];
    }

    if (userId === undefined || userId === null) {
        return [];
    }

    return Object.keys(fieldsFocusedBy).filter(
        /* eslint-disable-next-line eqeqeq -- Kennungen kommen mal als Zahl, mal als Text */
        path => fieldsFocusedBy[path] == userId
    );
}

/**
 * Wer blockiert diesen Pfad gerade?
 *
 * @param {string} path              der Pfad, der geöffnet werden soll
 * @param {Object} fieldsFocusedBy   Pfad => Benutzerkennung
 * @param {*}      currentUserId     die eigene Kennung; eigene Markierungen
 *                                   blockieren nicht
 * @returns {{user_id: *, path: string}|null}
 */
export function findConflictingFocus(path, fieldsFocusedBy, currentUserId) {
    if (! fieldsFocusedBy || typeof fieldsFocusedBy !== 'object') {
        return null;
    }

    for (const fremderPfad of Object.keys(fieldsFocusedBy)) {
        const userId = fieldsFocusedBy[fremderPfad];

        if (userId === undefined || userId === null) {
            continue;
        }

        /* eslint-disable-next-line eqeqeq -- Kennungen kommen mal als Zahl, mal als Text */
        if (currentUserId !== undefined && currentUserId !== null && userId == currentUserId) {
            continue;
        }

        if (scopesCollide(path, fremderPfad)) {
            return { user_id: userId, path: fremderPfad };
        }
    }

    return null;
}
